import { Observable, Subject } from 'rxjs';

const _createSubscriber = <E, R>(
  $ob: Observable<E>,
  handler: (value: E) => {
    isComplete?: boolean;
    isNext?: boolean;
    result: R;
  },
) => {
  const subject = new Subject<R>();

  $ob.subscribe({
    next(value) {
      const { isComplete = false, isNext = true, result } = handler(value);

      if (isComplete) {
        subject.complete();
      } else if (isNext) {
        subject.next(result);
      }
    },
  });

  return subject;
};

export const withTarget = <
  E extends Event,
  R extends With<Omit<E, 'target'>, { target: HTMLElement }>,
>(
  $ob: Observable<E>,
) => _createSubscriber($ob, value => ({ result: value as unknown as R }));

export const createIntersectionObserver = (
  element: Element,
  options?: IntersectionObserverInit,
): Observable<boolean> => {
  return new Observable<boolean>(subscriber => {
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => subscriber.next(entry.isIntersecting));
    }, options);

    observer.observe(element);

    return () => observer.disconnect();
  });
};

export const filterKey =
  (
    keyList: string[],
    options?: {
      conditionSpecialKey?(e: {
        alt: boolean;
        ctrl: boolean;
        meta: boolean;
        shift: boolean;
      }): boolean;
      exactCase?: boolean;
    },
  ) =>
  <E extends KeyboardEvent>($ob: Observable<E>) =>
    _createSubscriber($ob, e => {
      const { conditionSpecialKey = () => true, exactCase = false } =
        options ?? {};
      const _key = exactCase ? e.key : e.key.toLowerCase();
      const result =
        conditionSpecialKey({
          alt: e.altKey,
          ctrl: e.ctrlKey,
          meta: e.metaKey,
          shift: e.shiftKey,
        }) &&
        (exactCase ? keyList : keyList.map(key => key.toLowerCase())).some(
          key => key === _key,
        );

      return {
        result: e,
        isNext: result,
      };
    });
